import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  saveButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#993d13",
    },
  },
  backButton: {
    backgroundColor: "#707070",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#4d4d4d",
    },
  },
}));

const DeviationStatusTab = (props) => {
  const { addSponsorList, rowData, rowNetwork, updateNetworkData } = props;
  const classes = useStyles();
  const [deviationStatuses, setDeviationStatuses] = useState([]); // Local state like PO Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingStates, setSavingStates] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const network = addSponsorList?.data?.network || rowNetwork;
  const networkUrl = updateNetworkData?.ecosystemUrl || rowData?.networkURL;

  const fetchDeviationStatuses = async () => {
    setError(null);
    setLoading(true);

    try {
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchPDStatuses/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        const list = data?.deviationStatuses || [];
        const formattedStatuses = list.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));

        setDeviationStatuses(formattedStatuses);
      } else {
        console.error("Failed to fetch deviation statuses:", response.data?.message);
        setError(response.data?.message || "Failed to fetch deviation statuses.");
      }
    } catch (error) {
      console.error("Error fetching deviation statuses:", error);
      setError("An error occurred while fetching deviation statuses.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts - same pattern as Batch Status
  useEffect(() => {
    if (networkUrl && network) {
      fetchDeviationStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: Fetching is also handled by parent Tabs.jsx
  // This function is used for initial load and refreshing after add/delete operations

  const addDeviationStatus = async (index) => {
    if (savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const statusToAdd = deviationStatuses[index];
      if (!statusToAdd.name.trim()) {
        setError("Please provide a name for the Deviation status.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        projectDevelopmentStatuses: [
          {
            statusType: "New",
            statusName: statusToAdd.name,
            activated: statusToAdd.selected,
            objectType: "Deviation",
          },
        ],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/addPDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        fetchDeviationStatuses();
        props.setDeviationStatus && props.setDeviationStatus((prev) => !prev);
        
        // Update the state to mark the status as saved
        setDeviationStatuses((prev) =>
          prev.map((status, i) =>
            i === index
              ? { ...status, id: response.data?.data?.id, isNew: false }
              : status
          )
        );
      } else {
        console.error("Failed to add Deviation status:", response.data?.message);
        setError(response.data?.message || "Failed to add Deviation status.");
        // Remove unsaved item on failure
        setDeviationStatuses((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (error) {
      console.error("Error adding Deviation status:", error);
      setError("An error occurred while adding the Deviation status.");
      // Remove unsaved item on error
      setDeviationStatuses((prev) => prev.filter((_, i) => i !== index));
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleNextClick = async () => {
    try {
      const isAnyStatusSelected = deviationStatuses.filter(
        (status) => status.isUpdated
      );
      if (!isAnyStatusSelected.length) {
        props.handleNext();
        return;
      }
      if (deviationStatuses.every((status) => !status.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }

      setIsSaving(true);

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        projectStatuses: [],
        campaignStatuses: [],
        changeControlStatuses: [],
        deviationStatuses: isAnyStatusSelected.map((status) => ({
          statusID: status.id,
          statusName: status.name,
          statusType: status.type,
          activated: status.selected,
          markForDelete: false,
          newStatusName: status.newStatusName || null,
        })),
        inProcessTestStatuses: [],
        productReleaseTestStatuses: [],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        await fetchDeviationStatuses();
        if (props.handleNext) {
          props.handleNext();
        }
      } else {
        console.error("Failed to update deviation statuses:", response.data?.message);
        setErrorMessage(response.data?.message || "Failed to update deviation statuses.");
      }
    } catch (error) {
      console.error("Error updating deviation statuses:", error);
      setErrorMessage("An error occurred while updating deviation statuses. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      const statusToDelete = deviationStatuses[index];

      if (statusToDelete.isNew) {
        setDeviationStatuses((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        projectStatuses: [],
        campaignStatuses: [],
        changeControlStatuses: [],
        deviationStatuses: [
          {
            statusID: statusToDelete.id,
            statusName: statusToDelete.name,
            statusType: statusToDelete.type,
            activated: statusToDelete.selected,
            markForDelete: true,
            newStatusName: statusToDelete.newStatusName || null,
          },
        ],
        inProcessTestStatuses: [],
        productReleaseTestStatuses: [],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        await fetchDeviationStatuses();
        props.setDeviationStatus && props.setDeviationStatus((prev) => !prev);
        setDeviationStatuses((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error("Failed to delete Deviation status:", response.data?.message);
        setError(response.data?.message || "Failed to delete Deviation status.");
      }
    } catch (error) {
      console.error("Error deleting Deviation status:", error);
      setError("An error occurred while deleting the Deviation status.");
    }
  };

  const handleToggle = (index) => {
    const updated = [...deviationStatuses];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setDeviationStatuses(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...deviationStatuses];
    updated[index].name = value;
    setDeviationStatuses(updated);
  };

  const handleAddStatus = () => {
    setDeviationStatuses([
      ...deviationStatuses,
      {
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
  };

  const handleCancel = (index) => {
    const statusToCancel = deviationStatuses[index];

    if (statusToCancel.isNew) {
      setDeviationStatuses((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setDeviationStatuses((prev) =>
      prev.map((status, i) =>
        i === index
          ? {
              ...status,
              isEditing: false,
              name: status.originalName || status.name,
            }
          : status
      )
    );
  };

  return (
    <div className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Deviation Status</TableCell>
            <TableCell className={classes.headerCell}>Deviation Status ID</TableCell>
            <TableCell className={classes.headerCell}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={5}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : deviationStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Deviation Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            deviationStatuses.map((item, index) => (
              <TableRow key={item.id || index}>
              <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {item.isNew ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        placeholder="Type Text Here"
                        value={item.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{
                          onBeforeInput: (e) => {
                            const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                            if (!allowedChars.test(e.data)) {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                      <div className={classes.inlineActions}>
                        <IconButton
                          onClick={() => handleCancel(index)}
                          className={classes.iconBtn}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => addDeviationStatus(index)}
                          className={classes.iconBtn}
                          disabled={
                            !item.name?.trim() ||
                            deviationStatuses.some(
                              (s, i) =>
                                i !== index &&
                                s.name?.trim().toLowerCase() === item.name?.trim().toLowerCase()
                            ) || savingStates[index]
                          }
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    item.name
                  )}
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.id}</TableCell>
                <TableCell className={classes.bodyCell}>
                  {(item.isNew == true || item.type === "New") && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}

      <Button
        variant="outlined"
        onClick={handleAddStatus}
        className={classes.addButton}
      >
        <span className={classes.addIconCircle}>+</span>
        Add New Deviation Status
      </Button>

      <div className={classes.footer}>
        <Grid
          className={classes.backButton}
          onClick={() => {
            props.handleBack();
          }}
        >
          <ChevronLeftIcon />
          Back
        </Grid>
        <Grid
          className={classes.saveButton}
          onClick={() => {
            if (isUpdated) {
              handleNextClick();
            } else {
              props.handleNext();
            }
          }}
          style={{ 
            opacity: isSaving ? 0.7 : 1, 
            pointerEvents: isSaving ? 'none' : 'auto' 
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
              Saving...
            </>
          ) : (
            <>
              Save & Next
              <ChevronRightIcon />
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default DeviationStatusTab;

